import { FC } from "react";

import clsx from "clsx";
import { RenderWhen, TooltipIcon } from "../../../../../../../components";
import {
  AnalysisAdditionalInfo,
  AnalysisStatus
} from "../../../../../../../lib/enums";
import {
  useGetSampleAnalysisStatus,
  useLocalSampleSummaryParams
} from "../../../../../hooks";
import { isSampleAnalyzing } from "../../../../../util";
import { WarningIcon } from "../../icons";
import Analyzing from "./Analyzing";
import Configure, { Type } from "./Conigure";
import Loading from "./Loading";
import styles from "./card-subtitle.module.scss";

type Props = {
  additionalInfo?: string;
  displayStatus?: string;
  impactsFinalClassification?: boolean;
  hasData?: boolean;
  type?: Type;
  onAnalyze?: () => void;
};

const Subtitle: FC<Omit<Props, "impactsFinalClassification">> = ({
  children,
  additionalInfo,
  displayStatus,
  hasData,
  type,
  onAnalyze
}) => {
  const { hash } = useLocalSampleSummaryParams();
  const { isLoading } = useGetSampleAnalysisStatus(hash);

  const isOversized = additionalInfo === AnalysisAdditionalInfo.FILE_OVERSIZED;

  const handleAnalyze = () => {
    if (isOversized) return;
    onAnalyze?.();
  };

  const isCoreProcessing = additionalInfo === AnalysisAdditionalInfo.PROCESSING;
  const showAnalyzeButton =
    additionalInfo === AnalysisAdditionalInfo.NEVER_SUBMITED ||
    additionalInfo !== AnalysisAdditionalInfo.NOT_CONFIGURED ||
    displayStatus === AnalysisStatus.FAILED_UPLOAD;

  if (isSampleAnalyzing(displayStatus) || isCoreProcessing)
    return <Analyzing />;

  if (isLoading && !hasData) return <Loading />;

  if (hasData) return <>{children}</>;

  if (additionalInfo === AnalysisAdditionalInfo.NOT_CONFIGURED)
    return <Configure type={type ?? "INTEGRATION"} />;

  if (showAnalyzeButton)
    return (
      <span
        className={clsx(styles.analyze_button, isOversized && styles.disabled)}
        onClick={handleAnalyze}
        data-testid="report_summary_subheader_analyze_link"
      >
        Analyze
      </span>
    );

  return <></>;
};

const CardSubtitle: FC<Props> = ({
  children,
  additionalInfo,
  displayStatus,
  type,
  hasData,
  impactsFinalClassification = false,
  onAnalyze
}) => (
  <div className="flex align-center flex-gap-2">
    <Subtitle
      additionalInfo={additionalInfo}
      displayStatus={displayStatus}
      hasData={hasData}
      type={type}
      onAnalyze={onAnalyze}
    >
      {children}
    </Subtitle>
    <RenderWhen
      condition={additionalInfo === AnalysisAdditionalInfo.FILE_OVERSIZED}
    >
      <WarningIcon
        className={styles.icon}
        message="File size is over size limit."
      />
    </RenderWhen>
    <RenderWhen condition={!impactsFinalClassification}>
      <TooltipIcon
        className={styles.info_icon}
        icon="info-circle"
        message="Does not impact final classification."
      />
    </RenderWhen>
  </div>
);

export default CardSubtitle;
